import { v4 as uuidv4 } from "uuid";

import { getListCampaignConfig } from "./getListCampaignConfig";

import type { Sender } from "../../List/components/CampaignOverview/components/Sender";
import type { SenderIdentity } from "../../List/components/CampaignOverview/utils/senderConfirmation.util";
import type { ListPerson } from "../../List/schema/list";
import type { User } from "@/pages/Public/pages/Login/schema/login";

/**
 * Every sender the "configure who is sending" section can switch on: each user's LinkedIn and each
 * of their mailboxes, all off. Moved out of `ReadyToSend` and `Compose`, which held a copy each.
 */
export const buildDefaultCampaignSenders = (users: User[] | undefined): Sender[] =>
	(users ?? []).flatMap((user) => {
		const name = `${user.firstName} ${user.lastName}`;
		const base = { userId: user.id, active: false, name, deactivated: false };
		const names = { firstName: user.firstName, lastName: user.lastName };
		const linkedin: Sender[] = user.unipile?.isLoggedIn
			? [
					{
						...base,
						...names,
						type: "linkedin",
						emailAddress: user.email,
						emailType: "primary",
						uuid: uuidv4(),
					},
				]
			: [];
		const mailboxes: Sender[] = (user.emails ?? []).map((email) => ({
			...base,
			...names,
			type: "email",
			emailAddress: email.email,
			emailId: email.id || undefined,
			emailType: email.type,
			uuid: uuidv4(),
		}));

		return [...linkedin, ...mailboxes];
	});

/**
 * The senders the section shows for a lead: its own sender overrides, else the list campaign's
 * senders, laid over the defaults they do not repeat.
 */
export const mergeCurrentCampaignSenders = (
	defaults: Sender[],
	prospect: ListPerson | undefined
): Sender[] => {
	// Prospect-level sender overrides take priority over campaign-level senders; the fallback is the
	// list-level campaign config (not the prospect's custom config, which may have had senders
	// stripped out during save).
	const prospectSenders = prospect?.senders;
	const effective =
		prospectSenders && prospectSenders.length > 0
			? prospectSenders
			: prospect
				? getListCampaignConfig(prospect)?.senders
				: undefined;

	if (!effective || effective.length === 0) {
		return defaults;
	}

	const rest = defaults.filter(
		(d) =>
			!effective.find(
				(s) => s.userId === d.userId && s.type === d.type && s.emailAddress === d.emailAddress
			)
	);

	return [...rest, ...effective];
};

interface CampaignSendersForParams {
	/** The senders configured in the confirm dialog; they are written to the lead before it is sent. */
	override?: Sender[];
	prospect?: ListPerson;
	/** The campaign the lead is sent to (the picked one, or the lead's own). */
	campaign?: { senders?: Sender[] } | null;
}

/**
 * Who a campaign send for one lead goes out as (PREC-4312), in the API's order so the question
 * names the senders that will actually send:
 *   1. `resolveCampaignForListPerson` (api schedule/utils/resolve-campaign-config.util.ts) layer 1:
 *      a lead's custom campaignConfig as it is, its senders or none;
 *   2. layer 3 over layer 2: the lead's own senders (the dialog's, which are written to the lead
 *      first), else the senders of the campaign it is sent to, never another campaign of the lead;
 *   3. `resolveStepSenders` (resolve-step-senders.util.ts): only the active ones.
 * Review to send's "Send sequence", "Assign & send out" and "Confirm & send", Compose's "Send
 * Selected" and Reach out's "Add to campaign" ask about these before they start.
 */
export const campaignSendersFor = ({
	override,
	prospect,
	campaign,
}: CampaignSendersForParams): Sender[] => {
	const custom = prospect?.campaignConfig as { senders?: Sender[] } | null | undefined;
	const own = override ?? (prospect?.senders?.length ? prospect.senders : undefined);
	const senders = custom ? (custom.senders ?? []) : (own ?? campaign?.senders ?? []);

	return senders.filter((s) => s.active && !s.deactivated);
};

/** Campaign senders as the confirmation reads them. */
export const toSenderIdentities = (senders: Sender[]): SenderIdentity[] =>
	senders.map((s) => ({
		userId: s.userId,
		type: s.type === "linkedin" ? "linkedin" : "email",
		emailAddress: s.emailAddress,
		name: s.name,
	}));
